import * as React from 'react'
import { useCallback, useMemo } from 'react'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { Theme, useTheme } from '@mui/material/styles'

import { ENTITY_TYPES } from '@sales-center/common'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import {
  addLeadSectionLabels,
  addOppSectionLabels,
} from '@user/components/Settings/EntityFormSettings/constants'
import type { IEntityFormSetting } from '@user/store/entityFormSettings/typings'

import { FieldSettingsCardList } from './FieldSettingsCardList'

/**
 * Theme-derived styles for a section heading and its container.
 *
 * The section is wider and its heading larger and flush to the top under the
 * Tidal theme; both states are resolved here so the JSX carries none of it.
 *
 * @param _theme The active MUI theme.
 * @param isTidalThemeActive Whether the Tidal theme is active.
 */
const getStyles = (_theme: Theme, isTidalThemeActive: boolean) => ({
  container: {
    pb: 2,
    flexGrow: 1,
    maxWidth: isTidalThemeActive ? 766 : 700,
  },

  heading: { pt: isTidalThemeActive ? 0 : 2, mb: 2 },

  props: {
    headingVariant: isTidalThemeActive ? ('h5' as const) : ('h6' as const),
  },
})

type Props = {
  settingsBySection: Record<string, IEntityFormSetting[]>
  entityType: ENTITY_TYPES
  settings: IEntityFormSetting[]
}

/**
 * Renders the built-in field settings grouped into their form sections.
 *
 * Iterates the sections in `settingsBySection`, resolving each section's
 * display label from the entity-appropriate label map and skipping any section
 * that has no fields. Each section renders its own heading followed by a
 * `FieldSettingsCardList`.
 *
 * Reads the Tidal theme flag once for the whole section list rather than per
 * section instance, and resolves every theme-dependent style through
 * `getStyles`.
 *
 * @param settingsBySection Field settings keyed by their section id.
 * @param entityType Selects which section label map to use.
 * @param settings The flat, whole-entity field-setting list. Used to decide
 *   which sections have content to show, and passed down as `allSettings` so
 *   required-group validation can see fields outside the section being
 *   rendered.
 */
export function FieldSettingsSection(props: Props) {
  const { settingsBySection, entityType, settings } = props
  const isTidalThemeActive = useTidalThemeActive()
  const theme = useTheme()
  const styles = getStyles(theme, isTidalThemeActive)

  const sectionNames: string[] = Object.keys(settingsBySection)

  const getSectionLabel = useCallback(
    (sectionId: string) =>
      entityType === ENTITY_TYPES.Lead
        ? addLeadSectionLabels[sectionId]
        : addOppSectionLabels[sectionId],
    [entityType],
  )

  const showSection = useCallback(
    (sectionName: string) =>
      !!settings.find((setting) => setting.sectionId === sectionName),
    [settings],
  )

  const sections = useMemo(
    () =>
      sectionNames.map(
        (sectionName) =>
          showSection(sectionName) && (
            <Box key={sectionName} sx={styles.container}>
              <Typography
                variant={styles.props.headingVariant}
                sx={styles.heading}
              >
                {getSectionLabel(sectionName)}
              </Typography>
              <FieldSettingsCardList
                settings={settingsBySection[sectionName]}
                allSettings={settings}
                entityType={entityType}
              />
            </Box>
          ),
      ),
    [
      sectionNames,
      styles,
      showSection,
      getSectionLabel,
      settingsBySection,
      settings,
      entityType,
    ],
  )

  return <>{sections}</>
}
FieldSettingsSection.displayName = 'FieldSettingsSection'
